// ============================================================================
// DonutChart + chartCatColor (P-CE11) — SVG inline, zero dependance (GC-E)
// ============================================================================
// Port FIDELE du PoC components/dashboard/donut-chart.tsx (89 L @ SHA 7e40e4a).
// Chaque tranche est un cercle a stroke-dasharray (technique robuste : gere la
// tranche unique a 100 %). Les couleurs sont passees par tranche (tokens
// --chart-cat-1..8, deja en place dans index.css depuis E2). PAS de recharts.
//
// TRANSPOSITION (§GC-E, Regle #28) : `cn` de @/lib/utils -> jointure locale de
// classes ; l'anneau de fond `var(--muted)` (absent du parc gest) -> classe
// Tailwind `stroke-slate-200 dark:stroke-slate-700` (dark-aware, zero hex).
// Regle #4 : zero emoji.

import type { DonutSlice } from './DonutChart.utils';

export function DonutChart({
  slices,
  size = 180,
  thickness = 26,
  className,
  ariaLabel,
}: {
  slices: DonutSlice[];
  size?: number;
  thickness?: number;
  className?: string;
  ariaLabel?: string;
}) {
  const positive = slices.filter((s) => s.value > 0);
  const total = positive.reduce((s, x) => s + x.value, 0);
  if (total <= 0) return null;

  const cx = size / 2;
  const cy = size / 2;
  const r = (size - thickness) / 2;
  const circ = 2 * Math.PI * r;

  // Prefix-sum PUR des fractions (offset de chaque tranche) : calcul en amont du
  // JSX, sans reassignation en cours de rendu (regle react-compiler). offsets[i]
  // = somme des fractions AVANT i (le point de depart de la tranche i sur le cercle).
  const fracs = positive.map((s) => s.value / total);
  const offsets = fracs.map((_, i) => fracs.slice(0, i).reduce((a, b) => a + b, 0));

  return (
    <svg
      viewBox={`0 0 ${size} ${size}`}
      width={size}
      height={size}
      role="img"
      aria-label={
        ariaLabel ??
        positive.map((s) => `${s.label} ${Math.round((s.value / total) * 100)} %`).join(', ')
      }
      className={`shrink-0${className ? ` ${className}` : ''}`}
    >
      {/* Anneau de fond (rail) — visible quand une seule tranche couvre tout. */}
      <circle
        cx={cx}
        cy={cy}
        r={r}
        fill="none"
        strokeWidth={thickness}
        className="stroke-slate-200 dark:stroke-slate-700"
      />
      {positive.map((s, i) => {
        const dash = fracs[i] * circ;
        const offset = -offsets[i] * circ;
        return (
          <circle
            key={`${s.label}-${i}`}
            cx={cx}
            cy={cy}
            r={r}
            fill="none"
            stroke={s.color}
            strokeWidth={thickness}
            strokeDasharray={`${dash} ${circ - dash}`}
            strokeDashoffset={offset}
            transform={`rotate(-90 ${cx} ${cy})`}
          />
        );
      })}
    </svg>
  );
}
